/**
 * Shared presentational component for custom metric range filter fields.
 *
 * Renders a list of {@link RangeFilterField} components, one per org-level
 * custom metric. Used inside the "REPORT STATS" collapsible section in the
 * desktop filter drawer, mobile filter action sheet, and add-player inline
 * filter panel. Each consumer provides its own section header/wrapper; this
 * component only owns the inner field list.
 * @module features/scouting/components/CustomMetricFilterFields/CustomMetricFilterFieldsView
 */

import React, { memo } from "react";

import { DEFAULT_CUSTOM_STAT_RANGE } from "@/constants/filterDefaults";
import RangeFilterField from "@/components/organisms/shared/RangeFilterField";

/** Props for {@link CustomMetricFilterFieldsView}. */
interface CustomMetricFilterFieldsViewProps {
  /** Available org-level custom metrics to render as range filters. */
  readonly customMetrics: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
  /** Current range values keyed by metric id. */
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  /** Callback invoked when a metric's range changes. */
  readonly onSetCustomStatRange: (
    name: string,
    range: [number, number]
  ) => void;
  /** Whether dark mode is active — for portal-safe styling */
  readonly isDark?: boolean;
}

/**
 * Renders a {@link RangeFilterField} for each custom metric. Returns `null` when `customMetrics` is empty so consumers can skip the surrounding wrapper without an additional guard.
 * @param props - Component props
 * @param props.customMetrics - Available org-level custom metrics to render as range filters.
 * @param props.customStatRanges - Current range values keyed by metric id.
 * @param props.onSetCustomStatRange - Callback invoked when a metric's range changes.
 * @param props.isDark - Whether dark mode is active — for portal-safe styling
 */
const CustomMetricFilterFieldsView = ({
  customMetrics,
  customStatRanges,
  onSetCustomStatRange,
  isDark,
}: CustomMetricFilterFieldsViewProps) =>
  customMetrics.length === 0 ? null : (
    <>
      {customMetrics.map(metric => {
        const range = (customStatRanges[metric.id] ??
          DEFAULT_CUSTOM_STAT_RANGE) as [number, number];
        return (
          <RangeFilterField
            key={`custom-${metric.id}`}
            label={metric.displayName}
            value={range}
            minValue={DEFAULT_CUSTOM_STAT_RANGE[0]}
            maxValue={DEFAULT_CUSTOM_STAT_RANGE[1]}
            step={1}
            // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- Dynamic metric.id binding requires inline closure
            onChange={(r: [number, number]) =>
              onSetCustomStatRange(metric.id, r)
            }
            isDark={isDark}
          />
        );
      })}
    </>
  );

CustomMetricFilterFieldsView.displayName = "CustomMetricFilterFieldsView";

export default memo(CustomMetricFilterFieldsView);
